import {
  WebAppConfig,
  ApiGatewayConfig,
  AuthConfig,
  RumConfig,
} from "@amzn/afxtaco-types";

export enum SettingsFile {
  STANDARD = "settings.json",
}

/**
 * Retrieves the file which manages the configuration of the Web App, either from a local JSON file or one written to the root of the CloudFront distribution.
 * In local development, first attempts to load a user-specific override file (local-settings.user.json),
 * falling back to the default (local-settings.json) if the user file doesn't exist.
 * The Date.now() call ensures we are fetching the latest settings file rather than a cached version.
 * @param fileName - name of the settings file to fetch. By default fetches "settings.json"
 * @returns Path of the settings file to fetch, e.g. "/local-settings.user.json?12345679" for local development
 */
export async function fetchSettingsFile<T>(
  fileName: SettingsFile = SettingsFile.STANDARD,
): Promise<T> {
  if (import.meta.env.DEV) {
    // Try user-specific settings first, fall back to default.
    // Vite's dev server returns 200 with HTML for missing files (SPA fallback),
    // so we also verify the response is actually JSON before using it.
    const userFilePath = `/local-${fileName.replace(".json", ".user.json")}?${Date.now()}`;
    const userResponse = await fetch(userFilePath);
    const contentType = userResponse.headers.get("content-type") ?? "";
    if (userResponse.ok && contentType.includes("application/json")) {
      return userResponse.json();
    }
    const defaultFilePath = `/local-${fileName}?${Date.now()}`;
    return (await fetch(defaultFilePath)).json();
  }
  const filePath = `/${fileName}?${Date.now()}`;
  return (await fetch(filePath)).json();
}

class ConfigFetcher {
  private config?: WebAppConfig;

  /**
   * Force override the config for testing
   * @param config - overriding config
   */
  __forceSetConfig(config: WebAppConfig): void {
    this.config = config;
  }

  /**
   * Initialize the config file, or return the already initialized config file
   * @param configFile - an overriding config file to set
   * @returns The config file
   */
  async initialize(configFile?: WebAppConfig): Promise<WebAppConfig> {
    if (this.config !== undefined) return this.config;
    if (configFile) {
      this.config = configFile;
      return this.config;
    }
    const fetchedConfig = await fetchSettingsFile<WebAppConfig>();
    this.config = fetchedConfig;
    return this.config;
  }

  getApiGatewayConfig(): ApiGatewayConfig {
    if (!this.config) {
      throw new Error("Config not initialized");
    }
    return this.config?.apiGateway as ApiGatewayConfig;
  }

  getWssGatewayConfig(): ApiGatewayConfig {
    if (!this.config) {
      throw new Error("Config not initialized");
    }
    return this.config?.wssGateway as ApiGatewayConfig;
  }

  getAuthConfig(): AuthConfig {
    if (!this.config) {
      throw new Error("Config not initialized");
    }
    return this.config?.auth as AuthConfig;
  }

  getStage(): string {
    if (!this.config) {
      throw new Error("Config not initialized");
    }
    return this.config?.stage as string;
  }

  getRumConfig(): RumConfig | undefined {
    if (!this.config) {
      throw new Error("Config not initialized");
    }
    return this.config?.rum;
  }
}

export const config = new ConfigFetcher();
